import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import PlusIcon from 'packages/kakapo-ui-kit/assets/icons/PlusIcon';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import Tooltip from 'packages/kakapo-ui-kit/components/tooltips/Tooltip';

import CommentIcon from 'packages/kakapo-ui-kit/assets/icons/CommentIcon';
import EvaluationIcon from 'packages/kakapo-ui-kit/assets/icons/EvaluationIcon';
import FeedbackIcon from 'packages/kakapo-ui-kit/assets/icons/FeedbackIcon';
import GoalIcon from 'packages/kakapo-ui-kit/assets/icons/GoalIcon';

const Container = styled.div`
	padding: 10px 0px;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const Button = styled.div`
	padding: 10px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
	cursor: pointer;
	border-radius: 10px;
	position: relative;

	&:hover {
		background: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.1);

		&:active {
			background: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.25);
		}
	}
`;

const Icon = styled.div`
	background: ${(props) => props.theme.colors.primary};
	width: 20px;
	height: 20px;
	border-radius: 10px;

	svg {
		display: block;
	}
`;

const Relative = styled.div`
	position: relative;
`;

const Title = styled.div`
	color: ${(props) => props.theme.colors.primary};
	margin-top: 5px;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: 70px 70px 70px 70px;
	gap: 10px;
`;

const Block = styled(Flex)`
	background: #f1f3f5;
	border-radius: 6px;
	height: 70px;
	cursor: pointer;

	&:hover {
		background: #edeeef;
	}

	&:active {
		background: #e1e2e3;
	}
`;

const BlockTitle = styled.div`
	text-align: center;
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 10px;
	margin-top: 5px;
`;

function BlockButton(props: {
	icon: React.ReactNode;
	title: string;
	type: graphql.MomentType;
	onSelect: (type: graphql.MomentType) => void;
}) {
	return (
		<Block
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			onClick={() => props.onSelect(props.type)}
		>
			{props.icon}
			<BlockTitle>{props.title}</BlockTitle>
		</Block>
	);
}

export default function AddMomentButton(props: { onSelect: (type: graphql.MomentType) => void }) {
	const { t } = useTranslation();

	const [active, setActive] = useState(false);

	const onSelect = (type: graphql.MomentType) => {
		setActive(false);
		props.onSelect(type);
	};

	return (
		<Container>
			<Relative>
				{active && (
					<Tooltip
						title={t('common.settings.cycleTemplates.momentTypes.title')}
						grow
						onBlur={() => setActive(false)}
						styleContent={false}
					>
						<Grid>
							<BlockButton
								title={t('common.settings.cycleTemplates.momentTypes.evaluation')}
								icon={<EvaluationIcon />}
								onSelect={onSelect}
								type={graphql.MomentType.Evaluation}
							/>
							<BlockButton
								title={t('common.settings.cycleTemplates.momentTypes.feedback')}
								icon={<FeedbackIcon />}
								onSelect={onSelect}
								type={graphql.MomentType.Feedback}
							/>
							<BlockButton
								title={t('common.settings.cycleTemplates.momentTypes.goals')}
								icon={<GoalIcon />}
								onSelect={onSelect}
								type={graphql.MomentType.Goals}
							/>
							<BlockButton
								title={t('common.settings.cycleTemplates.momentTypes.message')}
								icon={<CommentIcon color="primary" />}
								onSelect={onSelect}
								type={graphql.MomentType.Message}
							/>
						</Grid>
					</Tooltip>
				)}

				<Button onClick={() => setActive(true)}>
					<Icon>
						<PlusIcon />
					</Icon>
					<Title>{t('common.settings.cycleTemplates.edit.addMoment')}</Title>
				</Button>
			</Relative>
		</Container>
	);
}
